/*
 * The footer's 10% welcome offer: the pop-up's sign-up (assets/email-popup.js) as an inline
 * form. Markup and the server gate are in sections/footer.liquid.
 *
 * Submit (Simple mode): validation, then EwOffers.claim() with place `footer` (Klaviyo
 * subscribe, stores the signup), then EwOffers.apply() with source `footer`. Klaviyo first: if
 * it fails, no code. The success step never shows the code; the welcome email carries it.
 *
 * On load the card follows the cart, not just the fact of a past sign-up (viewFor()):
 *   - the cart holds the stored signup's code            → the success step;
 *   - the cart holds any other code (THANKYOU15, a foreign one, the signup's code the buyer
 *     removed…)                                            → the card hides, as the pop-up does
 *                                                            for any code (D-45);
 *   - no code, and a live signup (not `removed`, not expired: the rule ew-cart-offers.js uses
 *     to apply it on the cart's next load)                 → the success step;
 *   - otherwise                                            → the form, so signing up again
 *                                                            re-applies a removed code.
 * Checked again on a back/forward-cache restore (back from the cart) and on a signup change in
 * another tab. A sign-up here fires `ew:signed-up`, which hides the pop-up's pill; one in the
 * pop-up flips this form the same way.
 *
 * EwOffers, EwKlaviyo and EwTrack are resolved when called. This deferred script runs before
 * ew-offers.js (the footer comes first in the document) while readyState is already
 * `interactive`, so the first sync waits for DOMContentLoaded, which follows every deferred
 * script, with `load` as the fallback once that has passed.
 */
(function () {
  const KEY_SUBSCRIBED = 'everwow:offer-subscribed';
  const KEY_SIGNUP = 'ew:signup';
  // The pop-up's shape check (TC-13, BE-12), on the lowercased address.
  const EMAIL = /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/;

  function track(event, params) {
    if (window.EwTrack) window.EwTrack.push(event, params || {});
  }

  // The stored signup (null when absent or expired).
  function storedSignup() {
    try {
      const offers = window.EwOffers;
      return (offers && offers.signup && offers.signup.get()) || null;
    } catch (error) {
      return null;
    }
  }

  /*
   * What the card shows: 'done', 'form' or 'hidden'. `cart` null means it could not be read;
   * then only the signup decides.
   */
  function viewFor(signup, cart) {
    const live = !!(signup && signup.code && signup.status !== 'removed');
    const codes =
      cart && Array.isArray(cart.discount_codes)
        ? cart.discount_codes.map(function (entry) {
            return String(entry.code || '').toUpperCase();
          })
        : [];
    if (codes.length) {
      if (live && codes.indexOf(signup.code.toUpperCase()) !== -1) {
        return 'done';
      }
      return 'hidden';
    }
    return live ? 'done' : 'form';
  }

  function readCart() {
    return fetch('/cart.js', {
      credentials: 'same-origin',
      cache: 'no-store',
    })
      .then(function (response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        return response.json();
      })
      .catch(function () {
        return null;
      });
  }

  // The pop-up's resultView(): what the form shows after claim() and apply().
  function resultView(status, applyResult) {
    const error = function (key) {
      return { view: 'error', error: key, line: false, applied: false };
    };
    if (status === 'subscribed') {
      switch (applyResult) {
        case 'applied':
          return { view: 'success', error: null, line: true, applied: true };
        case 'pending_gift':
        case 'network':
          return { view: 'success', error: null, line: true, applied: false };
        case 'not_applicable':
          return error('paused');
        default:
          return { view: 'success', error: null, line: false, applied: false };
      }
    }
    if (status === 'paused') return error('paused');
    return error('network');
  }

  function init() {
    const root = document.querySelector('[data-footer-offer]');
    if (!root || root.__ewFooterOffer) return;
    root.__ewFooterOffer = true;

    const form = root.querySelector('[data-footer-offer-form]');
    const done = root.querySelector('[data-footer-offer-done]');
    const email = root.querySelector('[data-footer-offer-email]');
    const honeypot = root.querySelector('[data-footer-offer-hp]');
    const errorLine = root.querySelector('[data-footer-offer-error]');
    const submit = root.querySelector('[data-footer-offer-submit]');
    const mode = root.getAttribute('data-mode');
    // The grid cell around the card: hidden whole, so no empty cell is left behind.
    const card = root.parentElement;
    let busy = false;

    function showError(key) {
      errorLine.textContent = root.getAttribute('data-i18n-error-' + key) || '';
      errorLine.hidden = false;
      email.setAttribute('aria-invalid', 'true');
      email.focus({ preventScroll: true });
    }

    function clearError() {
      errorLine.hidden = true;
      errorLine.textContent = '';
      email.removeAttribute('aria-invalid');
    }

    function setBusy(value) {
      busy = value;
      submit.disabled = value;
      if (value) submit.setAttribute('aria-busy', 'true');
      else submit.removeAttribute('aria-busy');
    }

    function showDone(line, focus) {
      const lineEl = done.querySelector('[data-footer-offer-line]');
      if (lineEl) lineEl.hidden = !line;
      form.hidden = true;
      done.hidden = false;
      if (focus) {
        const title = done.querySelector('h2');
        if (title) title.focus({ preventScroll: true });
      }
    }

    function showForm() {
      clearError();
      done.hidden = true;
      form.hidden = false;
    }

    // Apply viewFor() to the current cart. `fresh` skips the header's snapshot, which a
    // back/forward-cache restore brings back stale. A submit in flight wins over any sync.
    let syncs = 0;
    function sync(fresh) {
      if (busy) return;
      const ticket = ++syncs;
      const cart =
        !fresh && window.EwCartSnapshot
          ? Promise.resolve(window.EwCartSnapshot)
          : readCart();
      cart.then(function (current) {
        if (busy || ticket !== syncs) return;
        const view = viewFor(storedSignup(), current);
        card.hidden = view === 'hidden';
        if (view === 'done') showDone(true, false);
        else if (form.hidden) showForm();
      });
    }

    let started = false;
    function firstSync() {
      if (started) return;
      started = true;
      sync(false);
    }

    function succeed(view) {
      try {
        window.localStorage.setItem(KEY_SUBSCRIBED, '1');
      } catch (error) {
        // Blocked storage: the pop-up may still offer itself on the next page.
      }
      showDone(view.line, true);
      document.dispatchEvent(
        new CustomEvent('ew:signed-up', { detail: { place: 'footer' } }),
      );
    }

    function onSubmit(event) {
      event.preventDefault();
      if (busy) return;
      const address = String(email.value || '')
        .trim()
        .toLowerCase();
      if (!address) {
        showError('empty');
        return;
      }
      if (!EMAIL.test(address)) {
        showError('invalid');
        return;
      }
      clearError();

      const hp = honeypot && honeypot.value ? honeypot.value : '';
      if (hp && mode === 'simple') {
        // A bot: a success that sends nothing.
        showDone(true, true);
        return;
      }

      setBusy(true);
      track('ew_email_submit', { place: 'footer' });

      const offers = window.EwOffers;
      const claimed =
        offers && typeof offers.claim === 'function'
          ? offers.claim(address, { place: 'footer', hp: hp })
          : Promise.resolve({ status: 'network' });

      claimed
        .catch(function () {
          return { status: 'network' };
        })
        .then(function (claim) {
          const status = (claim && claim.status) || 'network';
          if (status !== 'subscribed' || !claim.code) {
            return { claim: claim || {}, status: status, applied: null };
          }
          return window.EwOffers.apply(claim.code, { source: 'footer' }).then(
            function (outcome) {
              return {
                claim: claim,
                status: status,
                applied: outcome && outcome.result,
              };
            },
            function () {
              return { claim: claim, status: status, applied: 'network' };
            },
          );
        })
        .then(function (outcome) {
          setBusy(false);
          const view = resultView(outcome.status, outcome.applied);
          track('ew_email_result', { place: 'footer', status: outcome.status });
          if (view.view === 'error') {
            showError(view.error);
            return;
          }
          if (view.applied) {
            track('ew_offer_apply', {
              offer: outcome.claim.offer || 'wc10',
              place: 'footer',
            });
          }
          try {
            if (window.EwKlaviyo) window.EwKlaviyo.identify(address);
          } catch (error) {
            // identify is fire-and-forget.
          }
          succeed(view);
        });
    }

    form.addEventListener('submit', onSubmit);
    email.addEventListener('input', function () {
      const value = String(email.value || '')
        .trim()
        .toLowerCase();
      if (!errorLine.hidden && EMAIL.test(value)) clearError();
    });

    // A sign-up in the pop-up on this page, or in another tab.
    document.addEventListener('ew:signed-up', function (event) {
      if (event.detail && event.detail.place === 'footer') return;
      if (form.hidden) return;
      showDone(true, false);
    });
    window.addEventListener('storage', function (event) {
      if (event.key === KEY_SIGNUP) sync(true);
    });
    // Back from the cart, where the code may have been removed: the restored page is stale.
    window.addEventListener('pageshow', function (event) {
      if (event.persisted) sync(true);
    });

    if (window.EwOffers) {
      firstSync();
    } else {
      document.addEventListener('DOMContentLoaded', firstSync, { once: true });
      window.addEventListener('load', firstSync, { once: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
